import { Button, Col, Form, Input, Modal, Row, Skeleton, Switch, Table } from 'antd';
import { ColumnType } from 'antd/lib/table';
import _ from 'lodash';
import React, { ReactElement, useState } from 'react';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import FormModal from '../../../shared/components/modal/FormModal';
import {
  APPLICATION_ID, DEFAULT_GUID, GUTTOR_SPACE, PHONE_NUMBER_REGEX, NAME_REGEX, REQUEST_PAYLOAD,
  authFeatureConstants,
  COMMON_ERROR,
  CONFIGURATION_VALIDATIONS,
} from '../../../shared/constants/commonConstants';
import { PROFILE_SAVECONTACT } from '../../../shared/constants/urlEndPoints';
import { FileDetail } from '../../../shared/models/file.profile.model';
import { SaveContactDetailRequest, SaveContactDetailResponse } from '../../../shared/models/hospitalProfile.model';
import SendRequest from '../../../shared/services/sendRequest.service';
import { RootState } from '../../../store';
import { fetchAdminSwitch, fetchEnableDisable, fetchHospitalProfile } from '../../../store/actions/hospitalAction';
import { fetchRolesMaster } from '../../../store/actions/lookUpAction';
import { HospitalState } from '../../../store/models/hospitalModel';
import { LookUpState } from '../../../store/models/lookUpModel';
import { canActivate, error, getInitialRoleConfig, prepareRoles, success } from '../../../utils/utility';
import UploadImage from './UploadImage';
import { PREFIXES } from '../../../shared/constants/crmConstants';

const AddContactForm = ({ visible, onCancel, title, data, profileId, imgUrl,
   contact, onAdminContact, onEnableDisableContact }: any): any => {
  const dispatch = useDispatch();
  const {adminSwitchAction, enableDisableClick} = useSelector((state: RootState): HospitalState => state.hospital);
  const [form] = Form.useForm();
  const isFormDisabled = !canActivate(authFeatureConstants.EDIT_USER);
  const [isShowContactForm, setIsShowContactForm] = useState(true);
  const { name: fullName, emailId: email, landlineNumber: landlineNo } = data || {} as any;
  const [fileData, setFileData] = useState({ fileGuid: '', fileName: '' });
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { roleMaster = [] } = useSelector((state: RootState): LookUpState => state.lookUp);
  const layout = {
    labelCol: { span: 24 },
    wrapperCol: { span: 24 },
  };
  let updatedFormVal = {};
  const [roles, setRoles] = useState([] as any);
  const adminRole = [{
    'typeName': 'Admin',
    'typeId': 4671,
  }];

  const onSaveContact = (): void => {
    const formVal = form.getFieldsValue();
    const { fullName: fName, department, emailId, landlineNumber, number, designation}: any = formVal;
    const roleKeys = Object.keys(formVal).filter((roleInfo: string): boolean => roleInfo?.startsWith('ROLES') && formVal[roleInfo]);
    setIsSaveLoading(true);
    saveContact({
      name: fName,
      department,
      emailId,
      landlineNumber,
      number,
      isActive: true,
      isAdmin: adminSwitchAction?.data,
      isEnabled: Object.keys(contact).some((key: any): any => contact[key] !== undefined) ?
        contact?.isEnabled : true,
      functions: prepareRoles(roleKeys),
      designation,
    });
  };

  const saveContact = async ({
    name,
    department,
    emailId,
    landlineNumber,
    number,
    isAdmin,
    isEnabled,
    isActive,
    functions,
    designation,
  }: any): Promise<any> => {
    const saveUrl = `${process.env.REACT_APP_BASE_URL}${PROFILE_SAVECONTACT}`;
    const payload: SaveContactDetailRequest = {
      applicationId: APPLICATION_ID,
      contactDetails: [
        {
          profileId,
          department,
          name,
          email: emailId,
          landlineNumber,
          number,
          isActive,
          isAdmin,
          isEnabled,
          contactId: contact?.contactId,
          contactUserId: contact?.contactUserId,
          imageReferenceId: contact?.imageReferenceId,
          fileName: fileData?.fileName,
          fileId: fileData && fileData.fileGuid ? fileData.fileGuid : contact.fileId || DEFAULT_GUID,
          functions: (isAdmin && functions?.length > 0) ? functions.concat(adminRole) : functions,
          designation,
        },
      ],
    };

    const res: SaveContactDetailResponse = await SendRequest.instance.post<any, any>(saveUrl, payload);
    if (res && res.isSuccess) {
      setIsShowContactForm(!isShowContactForm);
      dispatch(fetchRolesMaster());
      dispatch(fetchHospitalProfile(REQUEST_PAYLOAD));
      success(`Contact saved successfully!`);
      form.resetFields();
      onCancel();
    } else if (!res?.isSuccess && res?.errorMessage) {
      error(res.errorMessage || COMMON_ERROR);
    } else {
      error(`Couldn't save contact, please try again.`);
    }
    setIsSaveLoading(false);
  };

  const getFiles = ([{ fileName, fileGuid }]: FileDetail[]): void => {
    setFileData({ fileName, fileGuid });
  };

  useEffect((): any => {
    localStorage?.setItem('FileUploadType', 'Profile');  // setting FileType on mounting of the component  ---Bugfix 4304
    dispatch(fetchRolesMaster());
    form.setFieldsValue({
      fullName,
      emailId: email,
      landlineNumber: landlineNo,
    });
    return (): any => {
   localStorage?.removeItem('FileUploadType');  // removing filetypes on unmounting of the component
    };
  }, []);

  useEffect((): void => {
    if (contact) {
       updatedFormVal = {
        fullName: contact.name,
        emailId: contact.email,
        landlineNumber: contact.landlineNumber,
        number: contact.number,
        department: contact.department,
        designation: contact.designation,
      };
      contact?.functions?.map(({ typeName, typeId }: any): any => {
        updatedFormVal[`ROLES-${typeName}-${typeId}`] = true;
        return updatedFormVal;
      });
      form.setFieldsValue(updatedFormVal);
    }
  }, [contact]);

  useEffect((): any => {
    setRoles(getInitialRoleConfig(roleMaster, contact));
  }, [roleMaster, contact]);

  const checkboxOnChange = (ev: any, record: any): any => {
    const selectedRecord = record?.roles?.find(({ access }: any): boolean => access === 'W') || {};
    const selectedIndex = roles.findIndex(({ moduleID }: any): boolean => moduleID === record?.moduleID);
    if (!ev) {
      const { roleID, roleName } = selectedRecord;
      if (roleID && roleName) {
        form.resetFields([`ROLES-${roleName}-${roleID}`]);
        record.disabled = true;
        record.switchText = 'Read Only';
        roles.splice(selectedIndex, 1, record);
        const updatedRoles = [...roles];
        setRoles(updatedRoles);
        dispatch(fetchAdminSwitch(false));
      }
    } else {
      record.disabled = false;
      roles.splice(selectedIndex, 1, record);
      const updatedRoles = [...roles];
      setRoles(updatedRoles);
    }
  };

  const onSwitchChangeHandler = (ev: any , record: any): any => {
    const selectedIndex = roles.findIndex(({ moduleID }: any): boolean => moduleID === record?.moduleID);
    const selectedRecord = record?.roles?.find(({ access }: any): boolean => access === 'R') || {};
    const { roleID, roleName } = selectedRecord;
    if (ev && roleID && roleName) {
      const updatedFormFields = {};
      updatedFormFields[`ROLES-${roleName}-${roleID}`] = true;
      form.setFieldsValue({...updatedFormFields});
    }
    record.switchText = ev ? 'Edit Access' : 'Read Only';
    roles.splice(selectedIndex, 1, record);
    const updatedRoles = [...roles];
    setRoles(updatedRoles);
  };
  const onChangeSwitch = (): any => {
    if (adminSwitchAction?.data) {
      dispatch(fetchAdminSwitch(false));
    } else {
      dispatch(fetchAdminSwitch(true));
    }
    onAdminContact(updatedFormVal);
  };
  const onEnableDisableChange = (): any => {
    setIsModalVisible(true);
  };
  const onCancelConfirmation = (): any => {
    setIsModalVisible(false);
  };
  const onOkModal = (): any => {
    if (enableDisableClick?.data) {
      dispatch(fetchEnableDisable(false));
    } else {
      dispatch(fetchEnableDisable(true));
    }
    onEnableDisableContact(contact);
  };
  const columns: ColumnType<any>[] = [
    {
      title: 'Modules',
      key: 'moduleId',
      dataIndex: 'moduleName',
      width: '200px',
      render: (record: any): ReactElement => {
        return (
          <span>{ record === 'ABDM_HIU' ? 'HealthFlo (ABDM)' : _.capitalize(record)}</span>);
      },
    },
    {
      title: 'Access',
      key: 'Access',
      dataIndex: 'Access',
      render: (value: any, record: any, index: number): ReactElement => {
        const {roleName, roleID, access: writeAccess } = record?.roles?.find((ele: any): boolean => ele?.access === 'W') || {};
        const {roleName: readRoleName, roleID: readrRoleId, access: readAccess} = record?.roles?.find((ele: any): boolean => ele?.access === 'R') || {};

        return (
          roleName || readRoleName ?
            <Row gutter={GUTTOR_SPACE}>
              {readAccess &&
                <>
                <Col md={3}>
                <Form.Item name={`ROLES-${readRoleName}-${readrRoleId}`} valuePropName="checked">
                  <Switch size="small" onChange={(ev: any): any => checkboxOnChange(ev, record)} defaultChecked={!record?.disabled} />
                </Form.Item>
              </Col>
              <Col className="mt-xs">
                <strong>
                  <span className={`fsize_sm text-primary`}>Read Only</span>
                </strong>
              </Col>
              </>
              }
              {writeAccess &&
                <>
                  <Col md={3}>
                    <Form.Item name={`ROLES-${roleName}-${roleID}`} valuePropName="checked">
                      <Switch size="small" onChange={(ev: any): any => onSwitchChangeHandler(ev, record)} />
                    </Form.Item>
                  </Col>
                  <Col className="mt-xs">
                    <strong>
                      <span className={`fsize_sm ${record?.disabled ? 'gray-text' : 'text-primary'}`}>Edit Access</span>
                    </strong>
                  </Col>
                </>
              }
            </Row> :
            <></>
        );
      },
    },
  ];
  return (
    <FormModal visible={visible} onCancel={onCancel} title={title} isFooter={false} >
      {!roleMaster && <Skeleton active={true} paragraph={{ rows: 5 }} />}
      {form && roleMaster && (
        <Form form={form} autoComplete={'off'}
          {...layout} className="add-contact-form"
          name="basic"
          onFinish={onSaveContact}
        >
          <Row gutter={GUTTOR_SPACE}>
            <Col md={12}>
              <Row>
                <Form.Item
                  label="Full name"
                  name="fullName"
                  rules={[{ required: true, pattern: NAME_REGEX, message: 'Please enter valid full name!' }]}
                >
                  <Input placeholder="Full name" disabled={isFormDisabled} />
                </Form.Item>
              </Row>
              <Row>
                <Form.Item
                  label="Email ID"
                  name="emailId"
                  rules={[
                    { required: true, message: CONFIGURATION_VALIDATIONS.primaryMailIdPattern, type: 'email' },
                    { max: 50, message: CONFIGURATION_VALIDATIONS.emailIdCharLimit },
                  ]}
                >
                  <Input disabled={isFormDisabled} placeholder="Email id" />
                </Form.Item>
              </Row>
              <Row>
                <Form.Item
                  label="Mobile No."
                  name="number"
                  rules={[{ required: true, pattern: PHONE_NUMBER_REGEX, message: 'Please enter valid Mobile No.!' }]}
                >
                  <Input disabled={isFormDisabled} placeholder="Mobile No." prefix={PREFIXES.MOBILE} maxLength={10} />
                </Form.Item>
              </Row>
            </Col>
            <Col md={12}>
              <UploadImage
                disabled={isFormDisabled}
                getFiles={getFiles}
                imageUrl={imgUrl}
                imageTitle="Upload Profile Image"
                className="add-contact-img-upload"
              />
            </Col>
          </Row>
          <Row gutter={GUTTOR_SPACE}>
            <Col md={12}>
              <Form.Item
                label="Department"
                name="department"
                rules={[{ max: 20, message: 'Max 20 charecters long' }]}
              >
                <Input disabled={isFormDisabled} placeholder="Department" />
              </Form.Item>
            </Col>
            <Col md={12}>
              <Form.Item
                label="Designation"
                name="designation"
                rules={[{ max: 20, message: 'Max 20 charecters long' }]}
              >
                <Input disabled={isFormDisabled} placeholder="Designation" />
              </Form.Item>
            </Col>
          </Row>
          {
            <Table
              loading={!(roles?.length)}
              className="role-tbl mb-md mt-md"
              columns={columns}
              dataSource={roles || []}
              size="small"
              bordered={true}
              pagination={false}
              rowKey="moduleID"
            />
          }
          {
            !isFormDisabled &&
            <Row>
              <Col md={24}>
                  <Form.Item className="text-right mb-0">
                    <Row gutter={GUTTOR_SPACE}>
                      <Col>
                      <Switch size="small"
                        checked={adminSwitchAction?.data} onChange={onChangeSwitch}/>&nbsp;
                        Set Account As Admin
                        {!_.isEmpty(contact) &&
                          <Button type="default"
                            style={{ marginLeft: '20px' }}
                            onClick={onEnableDisableChange}>
                            {!_.isEmpty(contact) && contact?.isEnabled ? 'Disable' : 'Enable'}</Button>
                        }
                    </Col>
                      <Col className= {!_.isEmpty(contact) ? 'update-btn-profile' : 'save-btn-profile'}>
                        <Button loading={isSaveLoading} type="primary" htmlType="submit">{!_.isEmpty(contact) ? 'Update' : 'Save'}</Button>
                      </Col>
                    </Row>
                  </Form.Item>
              </Col>
            </Row>
          }
          <Modal
            visible={isModalVisible}
            onOk={onOkModal}
            onCancel={onCancelConfirmation}
          >
            <strong>Are you sure you want to {contact?.isEnabled ? 'disable' : 'enable'} this account?</strong>
          </Modal>
        </Form>
      )}
    </FormModal>
  );
};
export default AddContactForm;
